// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file ChatTokenizedText.tsx
 * @input Uses React, Badge, ChatComposerToken
 * @output Exports ChatTokenizedText component
 * @position Utility component for rendering tokenized text in message bubbles
 *
 * Parses a plain text string and replaces token values with inline badges.
 * Accepts the same ChatComposerToken type used by the input triggers,
 * so the same token definitions work for both input and display.
 *
 * SYNC: When modified, update:
 * - /packages/core/src/Chat/index.ts
 * - /packages/cli/assets/templates/blocks/components/ChatTokenizedText/ (block examples)
 */
import React from 'react';
import * as stylex from '@stylexjs/stylex';
import { Badge } from "../Badge/index.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

// =============================================================================
// Styles
// =============================================================================
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  root: {
    k1xSpc: "xt0psk2",
    $$css: true
  }
};

// =============================================================================
// Types
// =============================================================================

// =============================================================================
// Helpers
// =============================================================================

function isCustomToken(token) {
  return 'render' in token && typeof token.render === 'function';
}
function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
}

// =============================================================================
// Component
// =============================================================================

/**
 * Renders text with token values replaced by inline badges.
 *
 * Accepts the same `ChatComposerToken` type used by input triggers,
 * so you can share a single token definition between input and display.
 */
export function ChatTokenizedText({
  ref,
  children,
  tokens,
  xstyle,
  className,
  style,
  ...rest
}) {
  if (!children || !tokens || tokens.length === 0) {
    return /*#__PURE__*/_jsx("span", {
      ref: ref,
      ...mergeProps(themeProps('chat-tokenized-text'), stylex.props(styles.root, xstyle), className, style),
      ...rest,
      children: children ?? ''
    });
  }
  const parts = renderTokens(children, tokens);
  return /*#__PURE__*/_jsx("span", {
    ref: ref,
    ...mergeProps(themeProps('chat-tokenized-text'), stylex.props(styles.root, xstyle), className, style),
    ...rest,
    children: parts
  });
}
ChatTokenizedText.displayName = 'ChatTokenizedText';

// =============================================================================
// Render
// =============================================================================

function renderTokens(text, tokens) {
  const pattern = tokens.map(t => escapeRegExp(t.value)).join('|');
  const regex = new RegExp(`(${pattern})`, 'g');
  const tokenMap = new Map();
  for (const t of tokens) {
    tokenMap.set(t.value, t);
  }
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const matched = match[0];
    const token = tokenMap.get(matched);
    if (token) {
      parts.push(isCustomToken(token) ? /*#__PURE__*/_jsx("span", {
        children: token.render()
      }, `${matched}-${match.index}`) : /*#__PURE__*/_jsx(Badge, {
        label: token.label,
        variant: token.variant,
        icon: token.icon
      }, `${matched}-${match.index}`));
    }
    lastIndex = match.index + matched.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}